import { useMemo, useRef, useState } from 'react';
import { SortingDirection } from '@shared/utils/Table/TableSortingRules.interface';
const COLUMN_SEPARATOR_WIDTH = 2;
const MIN_COLUMN_FLEX_RATIO = 0.15; // Lower limit of column flex ratio
function getVisibleColumnsTotalFlex(columnWidths, sortedColumnIds, visibleColumnsIds) {
    let sum = 0;
    for (const id of sortedColumnIds) {
        if (!visibleColumnsIds.includes(id)) {
            continue;
        }
        const w = columnWidths.get(id);
        if (w !== undefined) {
            sum += w;
        }
    }
    return sum;
}
function pixelDeltaToFlexDelta(deltaPx, rowWidthPx, totalFlex) {
    if (rowWidthPx <= 0 || totalFlex <= 0) {
        return 0;
    }
    return (deltaPx * totalFlex) / rowWidthPx;
}
function getMinFlexForColumn(columnId, columns) {
    var _a;
    const col = columns.find((c) => c.id === columnId);
    const base = (_a = col === null || col === void 0 ? void 0 : col.defaultWidth) !== null && _a !== void 0 ? _a : 1;
    return Math.max(1, Math.round(base * MIN_COLUMN_FLEX_RATIO));
}
function clampResizeDeltaFlex(deltaFlex, leftFlex, rightFlex, minLeftFlex, minRightFlex) {
    const dMin = minLeftFlex - leftFlex;
    const dMax = rightFlex - minRightFlex;
    if (dMax < dMin) {
        return 0;
    }
    return Math.max(dMin, Math.min(dMax, deltaFlex));
}
export function useTableColumns(columns, settings, onVisibleColumnsChange, onSortedColumnIdsChange) {
    var _a, _b, _c, _d, _e;
    const ref = useRef(null);
    const [sortingRules, setSortingRules] = useState((_a = settings.sortingRules) !== null && _a !== void 0 ? _a : { columnId: columns[0].id, direction: SortingDirection.None });
    const [visibleColumnsIds, setVisibleColumnsIds] = useState((_b = settings.visibleColumnIds) !== null && _b !== void 0 ? _b : columns.filter((column) => column.visible).map((column) => column.id));
    const [sortedColumnIds, setSortedColumnIds] = useState((_c = settings.sortedColumnIds) !== null && _c !== void 0 ? _c : columns.map((column) => column.id));
    const [columnsWidth, setColumnsWidth] = useState(new Map(columns.map((column) => { var _a, _b; return [column.id, (_b = (_a = settings.columnsWidth) === null || _a === void 0 ? void 0 : _a.get(column.id)) !== null && _b !== void 0 ? _b : column.defaultWidth]; })));
    const [groupColumn, setGroupColumn] = useState(settings.groupColumnId
        ? ((_d = columns.find((column) => column.id === settings.groupColumnId && column.isAllowGrouping)) !== null && _d !== void 0 ? _d : null)
        : null);
    const [isColumnsResizing, setIsColumnsResizing] = useState(false);
    const [columnSeparatorOffsetX, setColumnSeparatorOffsetX] = useState(null);
    const [isShowTotals, setIsShowTotals] = useState((_e = settings.isShowTotals) !== null && _e !== void 0 ? _e : false);
    const setColumnSeparator = (columnId) => {
        var _a, _b, _c;
        const columnEl = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.getColumnElement(columnId);
        if (columnEl) {
            const parentWidth = (_c = (_b = columnEl.parentElement) === null || _b === void 0 ? void 0 : _b.offsetWidth) !== null && _c !== void 0 ? _c : 0;
            const columnOffsetX = columnEl.offsetLeft + columnEl.offsetWidth;
            setColumnSeparatorOffsetX(columnOffsetX + COLUMN_SEPARATOR_WIDTH > parentWidth
                ? parentWidth - COLUMN_SEPARATOR_WIDTH
                : columnOffsetX);
        }
        else {
            setColumnSeparatorOffsetX(null);
        }
    };
    const toggleColumnVisibility = (column) => {
        const columnId = column.id;
        setVisibleColumnsIds((prev) => {
            const newVisibleColumnsIds = [...prev];
            if (newVisibleColumnsIds.includes(columnId)) {
                newVisibleColumnsIds.splice(newVisibleColumnsIds.indexOf(columnId), 1);
            }
            else {
                newVisibleColumnsIds.push(columnId);
            }
            onVisibleColumnsChange === null || onVisibleColumnsChange === void 0 ? void 0 : onVisibleColumnsChange(newVisibleColumnsIds);
            return newVisibleColumnsIds;
        });
    };
    const resetToDefaultVisibility = () => {
        const newIds = columns.filter((column) => column.visible).map((column) => column.id);
        setVisibleColumnsIds(newIds);
        onVisibleColumnsChange === null || onVisibleColumnsChange === void 0 ? void 0 : onVisibleColumnsChange(newIds);
    };
    const onSortingChanged = (column) => {
        const columnId = column.id;
        if (sortingRules.columnId === columnId) {
            let newDirection;
            if (sortingRules.direction === SortingDirection.Ascending) {
                newDirection = SortingDirection.Descending;
            }
            else if (sortingRules.direction === SortingDirection.Descending) {
                newDirection = SortingDirection.None;
            }
            else {
                newDirection = SortingDirection.Ascending;
            }
            setSortingRules({
                columnId: columnId,
                direction: newDirection
            });
        }
        else {
            setSortingRules({ columnId: columnId, direction: SortingDirection.Ascending });
        }
    };
    const onOrderChanging = (_targetColumn, destinationColumn) => {
        if (isNullOrUndefined(destinationColumn)) {
            return;
        }
        const destinationColumnId = destinationColumn.id;
        setColumnSeparator(destinationColumnId);
    };
    const onOrderChanged = (targetColumn, destinationColumn) => {
        const targetColumnId = targetColumn.id;
        const destinationColumnId = destinationColumn.id;
        setColumnSeparator(null);
        if (targetColumnId === destinationColumnId) {
            return;
        }
        const removeIndex = sortedColumnIds.indexOf(targetColumnId);
        const insertIndex = sortedColumnIds.indexOf(destinationColumnId);
        const newSortedColumnIds = [...sortedColumnIds];
        newSortedColumnIds.splice(removeIndex, 1);
        newSortedColumnIds.splice(insertIndex, 0, targetColumnId);
        setSortedColumnIds(newSortedColumnIds);
        onSortedColumnIdsChange === null || onSortedColumnIdsChange === void 0 ? void 0 : onSortedColumnIdsChange(newSortedColumnIds);
    };
    const onWidthChanging = (leftColumn, rightColumn, deltaX) => {
        var _a, _b, _c, _d, _e;
        const leftColumnId = leftColumn.id;
        const rightColumnId = rightColumn.id;
        setIsColumnsResizing(true);
        setColumnSeparator(leftColumnId);
        const rowEl = (_b = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.getColumnElement(leftColumnId)) === null || _b === void 0 ? void 0 : _b.parentElement;
        const rowWidthPx = (_c = rowEl === null || rowEl === void 0 ? void 0 : rowEl.offsetWidth) !== null && _c !== void 0 ? _c : 0;
        const totalFlex = getVisibleColumnsTotalFlex(columnsWidth, sortedColumnIds, visibleColumnsIds);
        let deltaFlex = pixelDeltaToFlexDelta(deltaX, rowWidthPx, totalFlex);
        const leftFlex = (_d = columnsWidth.get(leftColumnId)) !== null && _d !== void 0 ? _d : 0;
        const rightFlex = (_e = columnsWidth.get(rightColumnId)) !== null && _e !== void 0 ? _e : 0;
        const minLeft = getMinFlexForColumn(leftColumnId, columns);
        const minRight = getMinFlexForColumn(rightColumnId, columns);
        deltaFlex = clampResizeDeltaFlex(deltaFlex, leftFlex, rightFlex, minLeft, minRight);
        setColumnsWidth((prev) => {
            const newColumnsWidth = new Map(prev);
            newColumnsWidth.set(leftColumnId, leftFlex + deltaFlex);
            newColumnsWidth.set(rightColumnId, rightFlex - deltaFlex);
            return newColumnsWidth;
        });
    };
    const onWidthChanged = (leftColumn, rightColumn, deltaX) => {
        onWidthChanging(leftColumn, rightColumn, deltaX);
        setIsColumnsResizing(false);
        setColumnSeparator(null);
    };
    const visibleColumns = useMemo(() => {
        const columnMap = new Map(columns.map((column) => [column.id, column]));
        return sortedColumnIds
            .filter((id) => visibleColumnsIds.includes(id))
            .map((id) => columnMap.get(id))
            .filter((column) => column !== undefined);
    }, [columns, sortedColumnIds, visibleColumnsIds]);
    return {
        ref,
        visibleColumns,
        columnsWidth,
        sortedColumnIds,
        sortingRules,
        columnSeparatorOffsetX,
        isColumnsResizing,
        groupColumn,
        setGroupColumn,
        isShowTotals,
        setIsShowTotals,
        toggleColumnVisibility,
        resetToDefaultVisibility,
        onSortingChanged,
        onOrderChanging,
        onOrderChanged,
        onWidthChanging,
        onWidthChanged,
        setVisibleColumnsIds,
        setSortedColumnIds,
    };
}
